/**
 * Renders a paper-styled live preview of the agreement body with placeholders
 * substituted. Mirrors the PDF layout enough to be useful for editing.
 *
 * Markdown-lite: # / ## / ### → headings, `-` → bullets, `---` → divider.
 */

import { useMemo } from 'react';
import type { TemplateVariable, PaymentMilestoneValue, CurrencyValue } from '@/types/table';

interface Props {
  body: string;
  variables: TemplateVariable[];
  values: Record<string, unknown>;
  hiddenVariables?: string[];
  primaryColor?: string;
  accentColor?: string;
  agreementNumber?: string;
  title?: string;
  agreementDate?: string;
  kind?: string;
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹', CAD: 'CA$', AUD: 'A$',
};

export function AgreementLivePreview({
  body,
  variables,
  values,
  hiddenVariables = [],
  primaryColor = '#0f172a',
  accentColor = '#a855f7',
  agreementNumber,
  title,
  agreementDate,
  kind,
}: Props) {
  const rendered = useMemo(() => substitute(body, variables, values, new Set(hiddenVariables)), [body, variables, values, hiddenVariables]);
  const lines = rendered.split('\n');
  return (
    <div className="rounded-md border bg-white text-[10.5px] leading-snug text-slate-800 shadow-sm overflow-hidden" style={{ minHeight: 600 }}>
      {/* Brand header band */}
      <div className="px-5 pt-3 pb-3" style={{ background: primaryColor, color: '#fff' }}>
        <div className="flex items-center justify-between text-[8.5px] font-semibold tracking-wider">
          <span>{kindLabel(kind)}</span>
          <span>{agreementNumber || '—'}</span>
        </div>
        <div className="font-bold text-lg leading-tight mt-1 truncate">{title || '(Untitled Agreement)'}</div>
        <div className="text-[9px] opacity-80 mt-0.5">Date: {agreementDate ? formatDate(agreementDate) : '—'}</div>
      </div>

      {/* Body */}
      <div className="px-5 py-4 space-y-1.5 max-h-[640px] overflow-y-auto">
        {lines.map((line, i) => renderLine(line, i, primaryColor, accentColor))}
      </div>
    </div>
  );
}

function renderLine(line: string, key: number, primary: string, _accent: string) {
  const trimmed = line.trim();
  if (/^-{3,}$/.test(trimmed)) return <hr key={key} className="my-2 border-slate-200" />;
  if (trimmed.startsWith('# '))   return <div key={key} className="font-bold mt-2.5" style={{ color: primary, fontSize: 13 }}>{trimmed.replace(/^#\s+/, '')}</div>;
  if (trimmed.startsWith('## '))  return <div key={key} className="font-bold mt-2"   style={{ color: primary, fontSize: 12 }}>{trimmed.replace(/^##\s+/, '')}</div>;
  if (trimmed.startsWith('### ')) return <div key={key} className="font-bold mt-1.5" style={{ color: primary, fontSize: 11 }}>{trimmed.replace(/^###\s+/, '')}</div>;
  if (trimmed.startsWith('- '))   return <div key={key} className="pl-3 relative"><span className="absolute left-0 top-0">•</span>{trimmed.slice(2)}</div>;
  if (trimmed === '')             return <div key={key} className="h-1" />;
  return <p key={key} className="text-slate-700">{line}</p>;
}

// ─────────────────────────────────────────────────────────────────────

// Mirrors the backend renderer's substituteAndClean: empty/hidden vars render
// as '' (no dashes), and lines that collapse to blank or a label-only fragment
// are dropped so the preview matches the generated PDF.
function substitute(body: string, schema: TemplateVariable[], values: Record<string, unknown>, hidden: Set<string>): string {
  const out: string[] = [];
  for (const line of body.split('\n')) {
    let placeholderCount = 0;
    let emptyCount = 0;
    const replaced = line.replace(/\{\{([A-Z0-9_]+)\}\}/g, (_m, key: string) => {
      placeholderCount++;
      const raw = values[key];
      const isEmpty = hidden.has(key) || raw === undefined || raw === null || raw === '';
      if (isEmpty) { emptyCount++; return ''; }
      const def = schema.find((v) => v.key === key);
      return formatValue(raw, def?.type);
    });
    if (placeholderCount > 0 && emptyCount > 0) {
      const cleaned = replaced
        .replace(/\s+([.,;:])/g, '$1')
        .replace(/\.\s*\.+/g, '.')
        .replace(/[,;]\s*$/g, '')
        .replace(/ {2,}/g, ' ')
        .replace(/[ \t]+$/g, '');
      const t = cleaned.trim();
      if (t === '' || /:\s*$/.test(t)) continue;
      out.push(cleaned);
      continue;
    }
    out.push(replaced);
  }
  return out.join('\n');
}

function formatValue(raw: unknown, type?: string): string {
  switch (type) {
    case 'date': return formatDate(raw as string | Date);
    case 'currency': {
      const v = raw as CurrencyValue | number;
      if (typeof v === 'number') return `$${v.toLocaleString()}`;
      if (v && 'amount' in v) {
        const sym = CURRENCY_SYMBOL[v.currency] || (v.currency + ' ');
        return `${sym}${Number(v.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      return '';
    }
    case 'percent':
    case 'number': return String(raw);
    case 'scopeList':
      return Array.isArray(raw) ? (raw as string[]).map((s) => `- ${s}`).join('\n') : '';
    case 'milestoneList':
      return Array.isArray(raw)
        ? (raw as PaymentMilestoneValue[])
            .map((m) => {
              const pct = m.percent !== undefined ? ` (${m.percent}%)` : '';
              const due = m.dueDate ? ` — due ${formatDate(m.dueDate)}` : '';
              return `- ${m.name || ''}${pct}${due}`;
            })
            .join('\n')
        : '';
    default: return String(raw);
  }
}

function formatDate(v: string | Date | undefined | null): string {
  if (!v) return '_____';
  const d = typeof v === 'string' ? new Date(v) : v;
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
}

function kindLabel(k?: string): string {
  switch (k) {
    case 'SDA': return 'SOFTWARE DEVELOPMENT AGREEMENT';
    case 'NDA': return 'NON-DISCLOSURE AGREEMENT';
    case 'CUSTOM': return 'AGREEMENT';
    default: return 'AGREEMENT';
  }
}
